import {
  getClientSettings,
  updateClientSettings,
} from 'api/generated/client-settings/client-settings';
import type { JsonNode } from 'api/generated/schemas';

/**
 * FE-side adapter for the monitoring settings stored on a client. The backend
 * persists this blob as a free-form JSON object (`getClientSettings` is typed
 * as `JsonNode` in the OpenAPI spec because the underlying column is a
 * generic JSON column with no @Schema annotation), so the typed shape lives
 * here on the consumer side. Keep this in sync with the keys the
 * monitoring-settings modal writes via `updateMonitoringSettings`.
 */
export interface ClientMonitoringSettings {
  cohort?: string;
  comparisonYears?: number[];
  inquiriesToApps?: string;
  applicationsToAdmits?: string;
  admitsToEnrollment?: string;
  [key: string]: unknown;
}

export interface ClientSettings {
  monitoring?: ClientMonitoringSettings;
  [key: string]: unknown;
}

const toNumericId = (id: string | number): number =>
  typeof id === 'number' ? id : Number(id);

/**
 * Typed wrapper around the generated `getClientSettings` function. Casts the
 * spec's `JsonNode` payload to the FE-side `ClientSettings` shape so consumer
 * code keeps its `.monitoring.cohort` typing without scattering `as` casts.
 */
export const fetchMonitoringSettings = async (
  userId: string,
  clientId: string | number,
): Promise<ClientSettings> => {
  const data = await getClientSettings(userId, toNumericId(clientId));

  return data as ClientSettings;
};

/**
 * Typed wrapper around the generated `updateClientSettings` mutation function.
 * Accepts a `ClientSettings` payload rather than a raw `JsonNode` so call
 * sites keep type safety on the keys they write.
 */
export const saveMonitoringSettings = async (
  userId: string,
  clientId: string | number,
  settings: ClientSettings,
): Promise<ClientSettings> => {
  const data = await updateClientSettings(
    userId,
    toNumericId(clientId),
    settings as JsonNode,
  );

  return data as ClientSettings;
};
